import { EntityType, LensRule, RuleCategory } from "generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Box, Button, TextField } from "@mui/material";
import { P } from "theme/typography";
import FormFieldAutoComplete from "components/FormFieldAutocomplete";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  browseConcepts,
  selectAllDescriptions,
  selectAllSubtypes,
  selectConceptCache,
  selectLensEditorForm,
  updateLensRule,
} from "redux/slices/lensEditor";
import { TypedSelect } from "components/PSelect";
import StyledAutocomplete from "components/StyledAutocomplete";
import { isValidPrintableASCII, parseSafeInt, QueryType, umlsToEntityType } from "Backend";
import { Visibility } from "@mui/icons-material";
import RequestStatus from "redux/slices/RequestStatus";
import { useSnackbar } from "notistack";

interface RuleProps {
  rule: LensRule;
  lensId: number;
  onClose?: () => void;
}

export const RuleEditor = ({ rule, lensId, onClose }: RuleProps) => {
  const form = useAppSelector(selectLensEditorForm);
  const allSubtypes = useAppSelector(selectAllSubtypes);
  const allDescriptions = useAppSelector(selectAllDescriptions);
  const conceptCache = useAppSelector(selectConceptCache);
  const dispatch = useAppDispatch();
  const [editorRule, setEditorRule] = useState(rule);
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const { enqueueSnackbar } = useSnackbar();

  const entityId = editorRule.entityQuery?.entityId;
  const concept = entityId ? conceptCache[entityId] : undefined;

  const dirty = editorRule !== rule;

  const determineQueryType = (rule: LensRule): QueryType => {
    if (rule.textQuery) {
      return "ContainsText";
    } else if (rule.entityQuery) {
      return "IncludesEntity";
    } else {
      return "ContainsText";
    }
  };

  const handleQueryTypeChange = (queryType: QueryType) => {
    if (queryType === determineQueryType(rule)) {
      // only update the query type portion of the editor rule
      setEditorRule({
        ...editorRule,
        textQuery: rule.textQuery,
        entityQuery: rule.entityQuery,
      });
    } else {
      setEditorRule({
        ...editorRule,
        textQuery:
          queryType === "ContainsText"
            ? { text: "New Text Rule", phraseSlop: undefined, fuzziness: undefined }
            : undefined,
        entityQuery: queryType === "IncludesEntity" ? { entityId: "", entityType: EntityType.Medication } : undefined,
      });
    }
  };

  useEffect(() => {
    const ruleTypeLower = rule.entityQuery?.entityType.toLowerCase();
    const mismatchType =
      concept !== undefined && concept !== null && concept.umlsEntityType.toLowerCase() !== ruleTypeLower;
    const actualType = concept?.umlsEntityType;

    if (mismatchType && actualType) {
      setEditorRule((prev) => {
        return {
          ...prev,
          entityQuery: {
            entityType: umlsToEntityType(actualType)! as EntityType,
            entityId: prev.entityQuery!.entityId,
          },
        };
      });
    }
  }, [editorRule.entityQuery?.entityId]);

  const onSave = () => {
    setStatus(RequestStatus.PENDING);
    dispatch(updateLensRule({ lensId: lensId, rule: editorRule }))
      .unwrap()
      .then(() => {
        enqueueSnackbar("Rule updated", { variant: "success" });
        if (onClose) {
          onClose();
        }
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar("Error updating rule", { variant: "error" });
        setStatus(RequestStatus.FAILED);
      });
  };

  return (
    <Box
      sx={{
        pt: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}>
      {/*Main Editor*/}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
        }}>
        {/*Heading Row*/}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}>
            <Box>
              <P variant="sm">Edit Rule</P>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}>
                <P variant="xsBold" sx={{ pr: 0.5 }}>
                  ID:
                </P>
                <P
                  variant="xs"
                  sx={{
                    fontFamily: "monospace",
                  }}>
                  {rule.ruleId}
                </P>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <TypedSelect<number>
              inputProps={{
                sx: {
                  minWidth: "20px",
                },
              }}
              label="Weight"
              value={editorRule.weight || 0}
              options={[0, 1]}
              onChange={(weight) => {
                setEditorRule((prev) => {
                  return { ...prev, weight: weight };
                });
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 1,
          }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 1,
            }}>
            <Box
              sx={{
                width: "100%",
              }}>
              {form !== undefined && (
                <FormFieldAutoComplete
                  selected={form.fields.find((field) => field.id === editorRule.fieldId)}
                  options={form.fields}
                  onChange={(field) => {
                    // update editorRule fieldId to field id
                    setEditorRule((prev) => {
                      return { ...prev, fieldId: field?.id ? field?.id : null };
                    });
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                width: "100%",
              }}>
              <TypedSelect<RuleCategory>
                formControlProps={{
                  fullWidth: true,
                }}
                label="Category"
                options={[RuleCategory.Product, RuleCategory.Injury, RuleCategory.Other]}
                value={editorRule.category}
                onChange={(cat) => {
                  setEditorRule((prev) => {
                    return { ...prev, category: cat };
                  });
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 1,
            }}>
            <Box
              sx={{
                width: "100%",
              }}>
              <StyledAutocomplete
                freeSolo
                multiple={false}
                value={editorRule.subtype}
                onInputChange={(_evt, value) => {
                  if (value !== null && value !== undefined) {
                    setEditorRule((prev) => {
                      return { ...prev, subtype: value.trim() };
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    color="secondary"
                    label="Subtype"
                    fullWidth
                    error={!isValidPrintableASCII(params.inputProps.value as string)}
                    helperText={
                      !isValidPrintableASCII(params.inputProps.value as string)
                        ? "Invalid input. Only printable ASCII characters are allowed."
                        : ""
                    }
                  />
                )}
                options={allSubtypes}
              />
            </Box>
            <Box
              sx={{
                width: "100%",
              }}>
              <StyledAutocomplete
                freeSolo
                multiple={false}
                value={editorRule.description}
                onInputChange={(_evt, value) => {
                  if (value !== null && value !== undefined) {
                    setEditorRule((prev) => {
                      return { ...prev, description: value.trim() };
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    color="secondary"
                    label="Description"
                    fullWidth
                    error={!isValidPrintableASCII(params.inputProps.value as string)}
                    helperText={
                      !isValidPrintableASCII(params.inputProps.value as string)
                        ? "Invalid input. Only printable ASCII characters are allowed."
                        : ""
                    }
                  />
                )}
                options={allDescriptions}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            mt: 1,
            alignItems: "center",
          }}>
          <P variant="sm">Rule Query</P>
        </Box>
        <Box
          sx={{
            mt: 0.5,
          }}>
          <TypedSelect<QueryType>
            label="Query Type"
            value={editorRule.textQuery ? "ContainsText" : "IncludesEntity"}
            options={["ContainsText", "IncludesEntity"]}
            onChange={(qt) => handleQueryTypeChange(qt)}
          />
        </Box>

        {editorRule.textQuery ? (
          <Box sx={{ width: "100%", display: "flex", gap: 1, flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
              <TextField
                fullWidth
                label="Query Text"
                value={editorRule.textQuery.text}
                onChange={(evt) => {
                  setEditorRule((prev) => {
                    return {
                      ...prev,
                      textQuery: {
                        ...prev.textQuery!,
                        text: evt.target.value,
                      },
                    };
                  });
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                label="Phrase Slop"
                value={editorRule.textQuery.phraseSlop}
                onChange={(evt) => {
                  setEditorRule((prev) => {
                    return {
                      ...prev,
                      textQuery: {
                        ...prev.textQuery!,
                        phraseSlop: parseSafeInt(evt.target.value),
                      },
                    };
                  });
                }}
              />
              <TextField
                fullWidth
                label="Fuzziness"
                value={editorRule.textQuery.fuzziness}
                onChange={(evt) => {
                  const fuzziness = parseInt(evt.target.value);
                  setEditorRule((prev) => {
                    return {
                      ...prev,
                      textQuery: {
                        ...prev.textQuery!,
                        fuzziness: isNaN(fuzziness) ? undefined : evt.target.value,
                      },
                    };
                  });
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}>
            <TextField
              label="Entity ID"
              value={editorRule.entityQuery?.entityId}
              error={editorRule.entityQuery?.entityId !== "" && concept === undefined}
              helperText={editorRule.entityQuery?.entityId !== "" && concept === undefined ? "No matching concept" : ""}
              onChange={(evt) => {
                setEditorRule((prev) => {
                  return {
                    ...prev,
                    entityQuery: {
                      ...prev.entityQuery!,
                      entityId: evt.target.value,
                    },
                  };
                });
              }}
            />
            {concept !== null && concept !== undefined && (
              <Button
                size="small"
                color="info"
                startIcon={<Visibility />}
                onClick={() => dispatch(browseConcepts({ concepts: [concept] }))}>
                {concept.canonicalName}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Action Buttons*/}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}>
        {onClose && <Button onClick={onClose}>Cancel</Button>}
        <LoadingButton
          variant="contained"
          disabled={
            !dirty || !isValidPrintableASCII(editorRule.subtype) || !isValidPrintableASCII(editorRule.description)
          }
          onClick={onSave}
          loading={status === RequestStatus.PENDING}>
          Save
        </LoadingButton>
      </Box>
    </Box>
  );
};
